import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageComponent } from '../../components/page/page.component';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { TranslateModule } from '@ngx-translate/core';
import { TitleComponent } from 'src/app/general/components/title/title.component';
import { ActionComponent } from 'src/app/general/components/action/action.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { CheckboxColumn, DataTableColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { LocalizationService } from 'src/app/general/services/localization.service';
import { LoadingMessage, ErrorResult, SuccessResult } from 'src/app/general/util/result';
import { Util } from 'src/app/general/util/util';
import { NavigationService } from '../../services/navigation.service';
import { SessionService } from '../../services/session.service';
import { Formatter } from '../../util/formatter';
import { BackendService } from '../../services/backend.service';
import { SubtitleComponent } from 'src/app/general/components/subtitle/subtitle.component';
import { ActionObserver } from 'src/app/general/components/action/action';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldDirective } from 'src/app/general/directives/field/field.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FieldContainerDirective } from 'src/app/general/directives/field/field-container.directive';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-menu-availability-page',
  standalone: true,
  imports: [
    CommonModule,
    PageComponent,
    TranslateModule,
    LoadableContentComponent,
    DataTableComponent,
    TitleComponent,
    SubtitleComponent,
    ActionComponent,
    MatButtonToggleModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    FieldContainerDirective,
    FieldDirective
  ],
  templateUrl: './menu-availability-page.component.html',
  styleUrls: ['./menu-availability-page.component.css']
})
export class MenuAvailabilityPageComponent {
  public loadingController: LoadingController;

  public menuEntries: Array<proto.waiternow.common.MenuEntryAvailabilityProto>;
  public unfilteredMenuEntries: Array<proto.waiternow.common.MenuEntryAvailabilityProto>;
  public menuEntriesColumns: Array<DataTableColumn<proto.waiternow.common.MenuEntryAvailabilityProto>>;

  public items: Array<proto.waiternow.common.ItemAvailabilityProto>;
  public unfilteredItems: Array<proto.waiternow.common.ItemAvailabilityProto>;
  public itemsColumns: Array<DataTableColumn<proto.waiternow.common.ItemAvailabilityProto>>;

  public showMenuEntries: boolean = true;
  public showItems: boolean = false;

  public searchFormControl: FormControl;

  constructor(
      private sessionService: SessionService,
      private localizationService: LocalizationService,
      private navigationService: NavigationService,
      private backendService: BackendService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.menuEntries = new Array();
    this.unfilteredMenuEntries = new Array();
    this.menuEntriesColumns = [
      new StringColumn(
        /* name= */ 'menuEntry',
        /* translateId= */ 'menu_entry',
        /* valueExtractor= */  menuEntry => "" + this.getText(menuEntry.name)),
      new CheckboxColumn(
        /* name= */ 'isAvailable',
        /* translateId= */ 'available',
        /* stateExtractor= */ menuEntry => !menuEntry.isUnavailable,
        /* onStateChanged= */  (menuEntry, isAvailable) => menuEntry.isUnavailable = !isAvailable),
    ];
    this.items = new Array();
    this.unfilteredItems = new Array();
    this.itemsColumns = [
      new StringColumn(
        /* name= */ 'item',
        /* translateId= */ 'menu_option',
        /* valueExtractor= */  item => "" + this.getText(item.name)),
      new StringColumn(
        /* name= */ 'price',
        /* translateId= */ 'price',
        /* valueExtractor= */  item => Formatter.formatMoney(item.price)),
      new CheckboxColumn(
        /* name= */ 'isAvailable',
        /* translateId= */ 'available',
        /* stateExtractor= */ item => !item.isUnavailable,
        /* onStateChanged= */  (item, isAvailable) => item.isUnavailable = !isAvailable),
    ];
    this.loadingController = new LoadingController();
    this.searchFormControl = new FormControl();
  }

  ngOnInit(): void {
    // TODO: if the menu availability page is refreshed, it would show empty data because locations
    // have not been fetched. The home page redirects to SelectLocationPage if no location has
    // been selected, and then SelectLocationPage redirects back to the home page once the location
    // is selected.
    if (!this.sessionService.getLocation()) {
      this.navigationService.goToHomePage();
      return;
    }
    this.searchFormControl.valueChanges.subscribe(() => {
      this.filterContent();
    });
    this.loadMenuAvailability();
  }

  private filterContent(): void {
    if (!this.searchFormControl.value) {
      this.menuEntries = this.unfilteredMenuEntries;
      this.items = this.unfilteredItems;
      return;
    }

    const searchText = this.searchFormControl.value.toLowerCase();

    this.menuEntries = [];
    this.items = [];
    for (const unfilteredMenuEntry of this.unfilteredMenuEntries) {
      if (this.getText(unfilteredMenuEntry.name).toLowerCase().indexOf(searchText) >= 0) {
        this.menuEntries.push(unfilteredMenuEntry);
      }
    }
    for (const unfilteredItem of this.unfilteredItems) {
      if (this.getText(unfilteredItem.name).toLowerCase().indexOf(searchText) >= 0) {
        this.items.push(unfilteredItem);
      }
    }
  }

  public loadMenuAvailability(): void {
    this.loadingController.onLoadingStarted(LoadingMessage.empty());
    this.backendService.getStructuredMenuAvailability(
      Util.safeString(this.sessionService.getLocation()?.id),
      /* onSuccess= */ response => {
        for (const menuEntry of response.menuEntriesAvailability || []) {
          // We need a proto builder (not the interface) because we update availability
          this.unfilteredMenuEntries.push(new proto.waiternow.common.MenuEntryAvailabilityProto(menuEntry));
        }
        this.unfilteredMenuEntries.sort((a, b) => this.getText(a.name).localeCompare(this.getText(b.name)));

        for (const item of response.itemsAvailability || []) {
          // We need a proto builder (not the interface) because we update availability
          this.unfilteredItems.push(new proto.waiternow.common.ItemAvailabilityProto(item));
        }
        this.unfilteredItems.sort((a, b) => this.getText(a.name).localeCompare(this.getText(b.name)));

        this.filterContent();
        this.loadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
      },
      /* onError */ error => {
        this.loadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_internal'));
      }
    );
  }

  public onMenuAvailabilityTypeChange(matButtonToggleChange: MatButtonToggleChange): void {
    if (matButtonToggleChange.value === 'menu_entries') {
      this.showMenuEntries = true;
      this.showItems = false;
    }
    else if (matButtonToggleChange.value === 'menu_options') {
      this.showMenuEntries = false;
      this.showItems = true;
    }
  }

  public updateMenuAvailability(actionObserver: ActionObserver): void {
    this.backendService.updateStructuredMenuAvailability(
      Util.safeString(this.sessionService.getLocation()?.id),
      this.menuEntries,
      this.items,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('updated'));
      },
      /* onError= */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_internal'));
      }
    );
  }

  public getLocationName(): string {
    const location = this.sessionService.getLocation();
    if (location) {
      return location.redundantData?.businessName + ' ' + location.name;
    }
    return '';
  }

  private getText(textProto: proto.waiternow.common.ITextProto | null | undefined): string {
    let str: string | null | undefined;
    if (this.localizationService.isSpanish()) {
      str = Formatter.getText(textProto, proto.waiternow.common.Language.SPANISH);
    }
    else {
      str = Formatter.getText(textProto, proto.waiternow.common.Language.ENGLISH);
    }
    if (!str) {
      str = textProto?.text;
    }
    return Util.safeString(str);
  }
}
